import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { IconLoader } from "../components/Icons";
import { useMsg } from "../hooks/useMsg";

interface PosPermissions {
  permissions: Record<string, string>;
  defaults: Record<string, string>;
}

const PERMISSION_KEYS = ["void_paid_receipt", "payment_writeoff", "order_cancel", "cash_operation", "shift_close", "manage_permissions", "price_override", "order_discount"];
const POS_ROLE_KEYS = ["employee", "supervisor", "manager"];

export default function OperationPermissions() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const canWrite = can(user, "pos_permissions", "write");
  const [posPerms, setPosPerms] = useState<PosPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const setMsg = useMsg();

  function errorText(e: any): string {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d: any) => d.msg || String(d)).join("; ");
    return t("common.error");
  }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get<PosPermissions>("/tenant/pos-permissions");
        setPosPerms(data);
      } catch (e) {
        setMsg({ type: "err", text: apiError(e, t("opPerms.loadError")) });
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  async function updatePosPermission(key: string, role: string) {
    if (!posPerms) return;
    setSaving(true);
    setMsg(null);
    try {
      const { data } = await api.put<PosPermissions>("/tenant/pos-permissions", {
        permissions: { [key]: role },
      });
      setPosPerms(data);
      setMsg({ type: "ok", text: t("companyProfile.saved") });
    } catch (e: any) {
      setMsg({ type: "err", text: errorText(e) || t("opPerms.saveError") });
    } finally {
      setSaving(false);
    }
  }

  if (loading || !posPerms) {
    return <div className="card" style={{ textAlign: "center", padding: 48 }}><IconLoader size={32} className="muted" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t("opPerms.title")}</h1>
        <p>{t("opPerms.subtitle")}</p>
      </div>

      <div className="card">
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{t("opPerms.cardTitle")}</h3>
        <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
          {t("opPerms.cardHint")}
        </p>
        <table>
          <tbody>
            {Object.keys(posPerms.permissions).map((key) => (
              <tr key={key}>
                <td>{PERMISSION_KEYS.includes(key) ? t(`opPerms.perm.${key}`) : key}</td>
                <td style={{ width: 220 }}>
                  <select
                    value={posPerms.permissions[key]}
                    disabled={saving || !canWrite}
                    onChange={(e) => void updatePosPermission(key, e.target.value)}
                  >
                    {POS_ROLE_KEYS.map((r) => (
                      <option key={r} value={r}>{t(`opPerms.role.${r}`)}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
